import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import dashboardService, { DashboardMetrics, RecentActivity } from '../services/dashboard.service';
import { toast } from 'react-toastify';

interface StatCard {
  title: string;
  value: number | undefined;
  description: string;
  icon: string;
  color: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics>({});
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [metricsData, activityData] = await Promise.all([
        dashboardService.getMetrics(),
        dashboardService.getRecentActivity(8),
      ]);
      setMetrics(metricsData);
      setRecentActivity(activityData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin      = user?.roles?.some((r: any) => r.name === 'admin');
  const isFirmAdmin  = user?.roles?.some((r: any) => r.name === 'firm_admin');
  const isExpertUser = user?.roles?.some((r: any) => r.name === 'expert_user');

  // Each role gets exactly 4 focused, non-overlapping cards
  const getStatCards = (): StatCard[] => {
    if (isAdmin) {
      return [
        { title: 'Active Cases',       value: metrics.active_cases,          description: 'Cases in progress',          icon: 'ri-folder-open-line',  color: 'blue'   },
        { title: 'Pending Approvals',  value: metrics.pending_firm_approvals, description: 'Firms awaiting approval',    icon: 'ri-time-line',         color: 'yellow' },
        { title: 'Upcoming Deadlines', value: metrics.upcoming_deadlines,     description: 'Due within 15 days',         icon: 'ri-alarm-warning-line',color: 'red'    },
        { title: 'New This Week',      value: metrics.recent_cases,           description: 'Cases created this week',    icon: 'ri-add-circle-line',   color: 'green'  },
      ];
    }
    if (isFirmAdmin) {
      return [
        { title: 'Active Cases',       value: metrics.active_cases,           description: 'Your firm\'s cases in progress',  icon: 'ri-folder-open-line',  color: 'blue'   },
        { title: 'Awaiting Approval',  value: metrics.firm_pending_approval,  description: 'Cases pending PVE review',        icon: 'ri-time-line',         color: 'yellow' },
        { title: 'Completed',          value: metrics.firm_completed_cases,   description: 'Evaluations completed',           icon: 'ri-checkbox-circle-line', color: 'green'},
        { title: 'Upcoming Deadlines', value: metrics.upcoming_deadlines,     description: 'Due within 15 days',              icon: 'ri-alarm-warning-line',color: 'red'    },
      ];
    }
    if (isExpertUser) {
      return [
        { title: 'My Active Cases',    value: metrics.active_assigned_cases,  description: 'Cases assigned to you',        icon: 'ri-folder-open-line',     color: 'blue'   },
        { title: 'Upcoming Meetings',  value: metrics.upcoming_meetings,      description: 'Scheduled meetings',           icon: 'ri-calendar-event-line',  color: 'purple' },
        { title: 'Upcoming Deadlines', value: metrics.upcoming_deadlines,     description: 'Due within 15 days',           icon: 'ri-alarm-warning-line',   color: 'red'    },
        { title: 'Completed',          value: metrics.expert_completed_cases, description: 'Cases you\'ve completed',      icon: 'ri-checkbox-circle-line', color: 'green'  },
      ];
    }
    // Regular user
    return [
      { title: 'My Cases',            value: metrics.my_cases,               description: 'Cases you\'ve created',       icon: 'ri-folder-line',          color: 'blue'   },
      { title: 'Awaiting Approval',   value: metrics.user_pending_approval,  description: 'Waiting for PVE review',      icon: 'ri-time-line',            color: 'yellow' },
      { title: 'Upcoming Deadlines',  value: metrics.upcoming_deadlines,     description: 'Due within 15 days',          icon: 'ri-alarm-warning-line',   color: 'red'    },
    ];
  };

  const colorMap: Record<string, { bg: string; text: string; light: string }> = {
    blue:   { bg: '#206bc4', text: '#206bc4', light: '#e8f0fb' },
    green:  { bg: '#2fb344', text: '#2fb344', light: '#e8f7ea' },
    yellow: { bg: '#f59f00', text: '#f59f00', light: '#fef6e4' },
    red:    { bg: '#d63939', text: '#d63939', light: '#fce8e8' },
    purple: { bg: '#7d4cdb', text: '#7d4cdb', light: '#f0ebfb' },
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      'Created':                   'warning',
      'Prospecting':               'info',
      'Approved':                  'success',
      'Retainer_Received':         'primary',
      'In_Person_Requested':       'secondary',
      'Virtual_Meeting_Requested': 'info',
      'Meeting_Scheduled':         'success',
      'Evaluation_Completed':      'success',
      'Rejected':                  'danger',
    };
    return map[status] || 'secondary';
  };

  const formatStatus = (status: string) =>
    status.replace(/_/g, ' ');

  const cards = getStatCards();

  return (
    <div className='page-wrapper'>
      <div className='page-header'>
        <div className='row align-items-center'>
          <div className='col'>
            <h2 className='page-title'>Dashboard</h2>
            <div className='text-muted mt-1'>Welcome back, {user?.name}</div>
          </div>
          <div className='col-auto ms-auto'>
            <button className='btn btn-primary' onClick={loadDashboardData}>
              <i className='ri-refresh-line me-1'></i>Refresh
            </button>
          </div>
        </div>
      </div>

      <div className='page-body'>
        {loading ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading…</span>
            </div>
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className='row row-deck row-cards'>
              {cards.map((card) => {
                const c = colorMap[card.color];
                return (
                  <div key={card.title} className={`col-sm-6 col-lg-${cards.length === 3 ? 4 : 3}`}>
                    <div className='card' style={{ borderTop: `3px solid ${c.bg}` }}>
                      <div className='card-body'>
                        <div className='d-flex align-items-center mb-3'>
                          <div
                            className='me-3 d-flex align-items-center justify-content-center rounded'
                            style={{ width: 40, height: 40, background: c.light }}
                          >
                            <i className={`${card.icon} fs-4`} style={{ color: c.text }}></i>
                          </div>
                          <div className='subheader text-uppercase fw-bold' style={{ fontSize: 11, color: '#6e7582' }}>
                            {card.title}
                          </div>
                        </div>
                        <div className='h1 mb-1' style={{ color: '#1d273b' }}>{card.value ?? 0}</div>
                        <div className='text-muted' style={{ fontSize: 13 }}>{card.description}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recent Activity */}
            <div className='row mt-4'>
              <div className='col-12'>
                <div className='card'>
                  <div className='card-header'>
                    <h3 className='card-title'>Recent Cases</h3>
                  </div>
                  {recentActivity.length > 0 ? (
                    <div className='table-responsive'>
                      <table className='table table-vcenter card-table'>
                        <thead>
                          <tr>
                            <th>Case</th>
                            <th>Firm</th>
                            <th>Service</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentActivity.map((activity) => (
                            <tr key={activity.id}>
                              <td>
                                <div className='fw-semibold'>{activity.case_name}</div>
                                <div className='text-muted' style={{ fontSize: 12 }}>
                                  By {activity.created_by}
                                </div>
                              </td>
                              <td className='text-muted'>{activity.firm_name}</td>
                              <td className='text-muted'>{activity.service_type}</td>
                              <td>
                                <span className={`badge bg-${getStatusColor(activity.status)}-lt`}>
                                  {formatStatus(activity.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className='card-body text-center py-5'>
                      <i className='ri-inbox-line fs-1 text-muted'></i>
                      <p className='text-muted mt-2 mb-0'>No recent cases</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
